/**
 * Overlay scrollbar scroll pane for bulk order (native bar hidden, thumb drawn on top).
 * Use this for all bulk-order scroll regions — do not reimplement overflow: auto.
 * Docs: impl/src/browser/bulkOrder/README.md
 *
 * When showScrollFade is on, bottom/right overlays render here; top fade is header-owned
 * (callers use onScrollFadeChange — see BulkOrderSphCylGrid sticky header).
 *
 * reserveScrollbarGutter (grid only): thumbs sit in sibling tracks when that axis overflows.
 * Catalog/recap must leave this off so overlay auto-hide behavior stays unchanged.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ScrollFadeOverlays from './ScrollFadeOverlays'
import useOverlayScrollbar, { getPaneFill } from './useOverlayScrollbar'
import {
  Viewport,
  GutterPack,
  ScrollerPane,
  Scroller,
  ScrollerContent,
  VerticalTrack,
  HorizontalTrack,
  GutterCorner,
  VerticalThumb,
  HorizontalThumb,
} from './BulkOrderScrollContainer.styles'

const SCROLL_FADE_THRESHOLD = 1
const TOP_FADE_SHOW_THRESHOLD = 4

const getScrollFadeState = (element, horizontal) => {
  if (!element) {
    return { top: false, bottom: false, right: false }
  }

  const {
    scrollTop,
    scrollHeight,
    clientHeight,
    scrollLeft,
    scrollWidth,
    clientWidth,
  } = element

  return {
    top: scrollTop > TOP_FADE_SHOW_THRESHOLD,
    bottom: scrollTop + clientHeight < scrollHeight - SCROLL_FADE_THRESHOLD,
    right:
      horizontal &&
      scrollLeft + clientWidth < scrollWidth - SCROLL_FADE_THRESHOLD,
  }
}

const getThumbStyle = (axis, thumb) =>
  axis === 'y' ? { height: `${thumb.size}px` } : { width: `${thumb.size}px` }

const BulkOrderScrollContainer = ({
  children,
  horizontal = false,
  className,
  showScrollFade = false,
  scrollFadeVariant = 'light',
  scrollFadeRightVariant,
  scrollFadeHeight,
  scrollFadeTopOffset,
  reserveScrollbarGutter = false,
  onScrollFadeChange,
}) => {
  const scrollerRef = useRef(null)
  const contentRef = useRef(null)
  const [fade, setFade] = useState({ top: false, bottom: false, right: false })

  const updateFade = useCallback(
    element => {
      setFade(current => {
        const nextFade = getScrollFadeState(element, horizontal)
        return current.top === nextFade.top &&
          current.bottom === nextFade.bottom &&
          current.right === nextFade.right
          ? current
          : nextFade
      })
    },
    [horizontal],
  )

  const onSync = useCallback(
    scroller => {
      if (showScrollFade) {
        updateFade(scroller)
      }
    },
    [showScrollFade, updateFade],
  )

  const {
    isActive,
    markActive,
    verticalThumb,
    horizontalThumb,
    verticalThumbNodeRef,
    horizontalThumbNodeRef,
    onScroll,
    beginDrag,
  } = useOverlayScrollbar({
    scrollerRef,
    contentRef,
    horizontal,
    reserveScrollbarGutter,
    onSync,
  })

  useEffect(() => {
    if (!onScrollFadeChange) return
    onScrollFadeChange(fade)
  }, [fade.top, fade, onScrollFadeChange])

  const hasVerticalGutter = Boolean(reserveScrollbarGutter && verticalThumb)
  const hasHorizontalGutter = Boolean(reserveScrollbarGutter && horizontalThumb)
  const { fillHeight } = getPaneFill({
    reserveScrollbarGutter,
    hasVerticalGutter,
    hasHorizontalGutter,
  })

  const scrollerPane = (
    <ScrollerPane
      $reserveGutter={reserveScrollbarGutter}
      $showRightBorder={
        reserveScrollbarGutter && !hasVerticalGutter && fade.right
      }
    >
      <Scroller ref={scrollerRef} onScroll={onScroll}>
        <ScrollerContent ref={contentRef} $reserveGutter={reserveScrollbarGutter}>
          {children}
        </ScrollerContent>
      </Scroller>

      {showScrollFade && (
        <ScrollFadeOverlays
          fade={fade}
          variant={scrollFadeVariant}
          rightVariant={scrollFadeRightVariant}
          height={scrollFadeHeight}
          topOffset={scrollFadeTopOffset}
        />
      )}
    </ScrollerPane>
  )

  const gutterTracks = (
    <>
      {hasVerticalGutter && (
        <VerticalTrack
          $joinCorner={hasHorizontalGutter}
          onMouseDown={event => beginDrag('y', event)}
        >
          <VerticalThumb
            ref={verticalThumbNodeRef}
            $inGutter
            style={getThumbStyle('y', verticalThumb)}
          />
        </VerticalTrack>
      )}

      {hasHorizontalGutter && (
        <HorizontalTrack
          $joinCorner={hasVerticalGutter}
          onMouseDown={event => beginDrag('x', event)}
        >
          <HorizontalThumb
            ref={horizontalThumbNodeRef}
            $inGutter
            style={getThumbStyle('x', horizontalThumb)}
          />
        </HorizontalTrack>
      )}

      {hasVerticalGutter && hasHorizontalGutter && <GutterCorner />}
    </>
  )

  return (
    <Viewport
      className={className}
      $isActive={isActive}
      $reserveGutter={reserveScrollbarGutter}
      $fillHeight={fillHeight}
      onMouseEnter={markActive}
    >
      {reserveScrollbarGutter ? (
        <GutterPack
          $fillHeight={fillHeight}
          $hasVerticalGutter={hasVerticalGutter}
          $hasHorizontalGutter={hasHorizontalGutter}
        >
          {scrollerPane}
          {gutterTracks}
        </GutterPack>
      ) : (
        scrollerPane
      )}

      {!reserveScrollbarGutter && verticalThumb && (
        <VerticalThumb
          ref={verticalThumbNodeRef}
          style={getThumbStyle('y', verticalThumb)}
          onMouseDown={event => beginDrag('y', event)}
        />
      )}

      {!reserveScrollbarGutter && horizontal && horizontalThumb && (
        <HorizontalThumb
          ref={horizontalThumbNodeRef}
          style={getThumbStyle('x', horizontalThumb)}
          onMouseDown={event => beginDrag('x', event)}
        />
      )}
    </Viewport>
  )
}

BulkOrderScrollContainer.propTypes = {
  children: PropTypes.node.isRequired,
  horizontal: PropTypes.bool,
  className: PropTypes.string,
  showScrollFade: PropTypes.bool,
  scrollFadeVariant: PropTypes.oneOf(['light', 'dark']),
  scrollFadeRightVariant: PropTypes.oneOf(['light', 'dark']),
  scrollFadeHeight: PropTypes.string,
  scrollFadeTopOffset: PropTypes.string,
  reserveScrollbarGutter: PropTypes.bool,
  onScrollFadeChange: PropTypes.func,
}

export default BulkOrderScrollContainer
